import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Preloader from '../../components/common/Preloader';
import { 
    getEmployeeById, 
    getEmployeeTimeEntries, 
    getHolidays, 
    getAllLeaves, 
    paySalary,
    saveDraftSalary
} from '../../services/adminService';
import { showError } from '../../utils/sweetAlert';
import { formatCurrency } from '../../utils/format';
import SalarySlipModal from '../../components/admin/SalarySlipModal';
import './PaySalary.css';

const PaySalary = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Parse query params
    const queryParams = new URLSearchParams(location.search);
    const monthParam = parseInt(queryParams.get('month')) || new Date().getMonth() + 1;
    const yearParam = parseInt(queryParams.get('year')) || new Date().getFullYear();
    const isEditMode = queryParams.get('edit') === 'true';

    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [showSlipModal, setShowSlipModal] = useState(false);
    
    // Payment Modal State
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [transactionId, setTransactionId] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
    
    // Employee Data
    const [employee, setEmployee] = useState(null);
    
    // Payroll Data
    const [baseSalary, setBaseSalary] = useState(0);
    const [perDaySalary, setPerDaySalary] = useState(0);
    
    // Attendance & Leave Data
    const [dutyDays, setDutyDays] = useState(0);
    const [standardDutyDays, setStandardDutyDays] = useState(26);
    const [holidaysCount, setHolidaysCount] = useState(0);
    
    const [paidLeaves, setPaidLeaves] = useState(0);
    const [unpaidLeaveDays, setUnpaidLeaveDays] = useState(0);
    const [unpaidLeaveDeduction, setUnpaidLeaveDeduction] = useState(0);
    
    // Half Days
    const [halfDays, setHalfDays] = useState(0);
    const [halfDayDeduction, setHalfDayDeduction] = useState(0);
    
    // Manual Adjustments
    const [allowances, setAllowances] = useState(0);
    const [manualDeductions, setManualDeductions] = useState(0);

    // Final
    const [exactNetSalary, setExactNetSalary] = useState(0);
    const [netSalary, setNetSalary] = useState(0);
    const [roundOff, setRoundOff] = useState(0);
    const [justPaid, setJustPaid] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, monthParam, yearParam]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // 1. Fetch Employee
            const empData = await getEmployeeById(id);
            setEmployee(empData);
            
            const salary = empData.salary || 0;
            setBaseSalary(salary);
            const dailyRate = salary / 30;
            setPerDaySalary(dailyRate);

            // 2. Fetch Time Entries to count Half Days
            const entries = await getEmployeeTimeEntries(id, monthParam, yearParam);

            // Calculate Half Days
            // 1st Half: 10:00 AM to 1:30 PM (Grace: 10:30 AM to 1:00 PM)
            // 2nd Half: 2:00 PM to 7:00 PM (Grace: 2:30 PM to 6:30 PM)
            let calculatedHalfDays = 0;
            
            entries.forEach(e => {
                if (!e.loginTime) return;
                const login = new Date(e.loginTime);
                const logout = e.logoutTime ? new Date(e.logoutTime) : null;
                
                const loginMins = login.getHours() * 60 + login.getMinutes();
                const logoutMins = logout ? (logout.getHours() * 60 + logout.getMinutes()) : 0;
                
                // 10:30 AM = 630 mins, 1:00 PM = 780 mins
                const completedFirstHalf = loginMins <= 630 && logoutMins >= 780;
                
                // 2:30 PM = 870 mins, 6:30 PM = 1110 mins
                const completedSecondHalf = loginMins <= 870 && logoutMins >= 1110;
                
                if (completedFirstHalf && completedSecondHalf) {
                    // Full day completed
                } else if (completedFirstHalf || completedSecondHalf) {
                    // Missed one half
                    calculatedHalfDays += 1;
                } else {
                    // Missed both halves
                    calculatedHalfDays += 2;
                }
            });
            setHalfDays(calculatedHalfDays);

            // 3. Fetch Holidays for the month
            const allHolidays = await getHolidays();
            const monthHolidays = allHolidays.filter(h => {
                const d = new Date(h.date);
                return d.getMonth() + 1 === monthParam && d.getFullYear() === yearParam && h.status !== 'withdrawn';
            });
            const defaultHolidaysCount = monthHolidays.length;
            setHolidaysCount(defaultHolidaysCount);

            // Calculate default duty days dynamically
            const daysInMonth = new Date(yearParam, monthParam, 0).getDate();
            let sundays = 0;
            for (let i = 1; i <= daysInMonth; i++) {
                const date = new Date(yearParam, monthParam - 1, i);
                if (date.getDay() === 0) sundays++;
            }
            let defaultDutyDays = daysInMonth - sundays - defaultHolidaysCount;
            setStandardDutyDays(defaultDutyDays);
            setDutyDays(defaultDutyDays);

            // 4. Fetch Leaves for the employee for the month
            const allLeaves = await getAllLeaves();
            const employeeLeaves = allLeaves.filter(l => 
                l.employee._id === id && 
                l.status === 'approved'
            );

            // Count total leave days in this specific month
            let totalLeaveDays = 0;
            employeeLeaves.forEach(leave => {
                const start = new Date(leave.startDate);
                const end = new Date(leave.endDate);
                
                // Iterate through each day of the leave
                let current = new Date(start);
                while (current <= end) {
                    if (current.getMonth() + 1 === monthParam && current.getFullYear() === yearParam) {
                        totalLeaveDays++;
                    }
                    current.setDate(current.getDate() + 1);
                }
            });

            // 5. Apply Business Rules
            let pLeaves = totalLeaveDays > 0 ? 1 : 0;
            let unpaidDays = totalLeaveDays > 1 ? totalLeaveDays - 1 : 0;
            setPaidLeaves(pLeaves);
            setUnpaidLeaveDays(unpaidDays);
            
            // Unpaid leave deduction is calculated by useEffect, but we need it now to reverse engineer manualDeductions
            const calculatedUnpaidLeaveDeduction = unpaidDays * dailyRate;
            const calculatedHalfDayDeduction = calculatedHalfDays * (dailyRate / 2);

            // 6. Fetch Existing Draft/Record
            const { getPayrollStatus } = await import('../../services/adminService');
            const payrolls = await getPayrollStatus(monthParam, yearParam);
            const existing = payrolls.find(p => p.employee === id);
            
            if (existing) {
                if (existing.basicSalary) {
                    setBaseSalary(existing.basicSalary);
                    setPerDaySalary(existing.basicSalary / 30);
                }
                
                const isPaid = existing.status === 'Paid';
                const resolveValue = (existingVal, defaultVal) => {
                    if (isPaid) return existingVal !== undefined ? existingVal : defaultVal;
                    return (existingVal !== undefined && existingVal !== 0) ? existingVal : defaultVal;
                };
                
                setDutyDays(resolveValue(existing.dutyDays, defaultDutyDays));
                setHolidaysCount(resolveValue(existing.paidHolidays, defaultHolidaysCount));
                setPaidLeaves(resolveValue(existing.paidLeaves, pLeaves));
                setUnpaidLeaveDays(resolveValue(existing.unpaidLeaves, unpaidDays));
                setHalfDays(resolveValue(existing.halfDays, calculatedHalfDays));

                if (existing.manualAllowances !== undefined) {
                    setAllowances(existing.manualAllowances);
                } else if (existing.allowances !== undefined) {
                    // Fallback to old approximated method if detailed field not present
                    setAllowances(Math.round(existing.allowances));
                }

                if (existing.manualDeductions !== undefined) {
                    setManualDeductions(existing.manualDeductions);
                } else if (existing.deductions !== undefined) {
                    // Fallback to old approximated method if detailed field not present
                    let manualDeds = existing.deductions - calculatedUnpaidLeaveDeduction - calculatedHalfDayDeduction;
                    setManualDeductions(Math.round(manualDeds > 0 ? manualDeds : 0));
                }

                if (existing.transactionId) {
                    setTransactionId(existing.transactionId);
                }
                if (existing.paymentDate) {
                    setPaymentDate(existing.paymentDate);
                }
                if (existing.issueDate) {
                    setIssueDate(existing.issueDate);
                }
            }

        } catch (err) {
            showError('Failed to load payroll data', err.message);
        } finally {
            setLoading(false);
        }
    };

    // Recalculate Deductions and Net Salary whenever variables change
    useEffect(() => {
        setUnpaidLeaveDeduction(unpaidLeaveDays * perDaySalary);
        setHalfDayDeduction(halfDays * (perDaySalary / 2));
    }, [unpaidLeaveDays, halfDays, perDaySalary]);

    // Recalculate Net Salary and auto-save draft
    useEffect(() => {
        const net = baseSalary - unpaidLeaveDeduction - halfDayDeduction + allowances - manualDeductions;
        const calculatedNet = net > 0 ? net : 0;
        setExactNetSalary(calculatedNet);
        
        const roundedNet = Math.round(calculatedNet);
        setNetSalary(roundedNet);
        
        const currentRoundOff = roundedNet - calculatedNet;
        setRoundOff(currentRoundOff);
        
        // Auto-save draft so that "Preview" can fetch it
        if (!loading && employee) {
            const saveDraft = setTimeout(async () => {
                try {
                    let finalAllowances = allowances;
                    let finalDeductions = unpaidLeaveDeduction + halfDayDeduction + manualDeductions;
                    
                    if (currentRoundOff > 0) finalAllowances += currentRoundOff;
                    if (currentRoundOff < 0) finalDeductions += Math.abs(currentRoundOff);

                    await saveDraftSalary({
                        employeeId: employee._id,
                        month: monthParam,
                        year: yearParam,
                        basicSalary: baseSalary,
                        allowances: finalAllowances,
                        deductions: finalDeductions,
                        dutyDays,
                        paidHolidays: holidaysCount,
                        paidLeaves,
                        unpaidLeaves: unpaidLeaveDays,
                        halfDays,
                        manualAllowances: allowances,
                        manualDeductions,
                        transactionId,
                        paymentDate,
                        issueDate
                    });
                } catch (err) {
                    console.error("Failed to save draft", err);
                }
            }, 1000); // 1-second debounce
            
            return () => clearTimeout(saveDraft);
        }
    }, [baseSalary, unpaidLeaveDeduction, halfDayDeduction, allowances, manualDeductions, loading, employee, monthParam, yearParam, transactionId, paymentDate, dutyDays, holidaysCount, paidLeaves, unpaidLeaveDays, halfDays, issueDate]);

    const handleProcessPayment = () => {
        setShowPaymentModal(true);
    };

    const confirmPayment = async (e) => {
        e.preventDefault();
        setProcessing(true);
        try {
            // The backend expects total deductions
            let finalAllowances = allowances;
            let finalDeductions = unpaidLeaveDeduction + halfDayDeduction + manualDeductions;
            
            if (roundOff > 0) finalAllowances += roundOff;
            if (roundOff < 0) finalDeductions += Math.abs(roundOff);
            
            await paySalary({
                employeeId: employee._id,
                month: monthParam,
                year: yearParam,
                basicSalary: baseSalary,
                allowances: finalAllowances,
                deductions: finalDeductions,
                dutyDays,
                paidHolidays: holidaysCount,
                paidLeaves,
                unpaidLeaves: unpaidLeaveDays,
                halfDays,
                manualAllowances: allowances,
                manualDeductions,
                transactionId,
                paymentDate,
                issueDate
            });
            
            setShowPaymentModal(false);
            setJustPaid(true);
            setShowSlipModal(true);
            
        } catch (err) {
            showError('Payment Failed', err.message);
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <Preloader />;
    if (!employee) return <div>Employee not found</div>;

    const monthName = new Date(yearParam, monthParam - 1).toLocaleString('default', { month: 'long' });

    return (
        <div className="pay-salary-container">
            <div className="pay-salary-header">
                <button onClick={() => navigate(`/admin/employees/${id}`)} className="btn-back">
                    ← Back to Profile
                </button>
                <h1>Process Salary: {employee.name}</h1>
            </div>

            <div className="info-badges">
                <div className="badge blue">📅 Month: {monthName} {yearParam}</div>
                <div className="badge purple">💼 Base Salary: {formatCurrency(baseSalary)}</div>
                <div className="badge green">💵 Per Day (÷30): {formatCurrency(perDaySalary)}</div>
            </div>

            <div className="pay-salary-grid">
                {/* Left Side: Breakdown */}
                <div className="ps-card">
                    <h2>Salary Breakdown</h2>
                    
                    <table className="breakdown-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th>Details</th>
                                <th style={{textAlign: 'right'}}>Amount (INR)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Monthly Base Salary</td>
                                <td>
                                    <input 
                                        type="number" 
                                        className="inline-input" 
                                        value={baseSalary === '' ? '' : baseSalary} 
                                        onChange={(e) => {
                                            const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                            setBaseSalary(val);
                                            setPerDaySalary(val / 30);
                                        }} 
                                    />
                                </td>
                                <td className="amount">
                                    <input 
                                        type="number" 
                                        className="inline-input text-right" 
                                        style={{ width: '80px', textAlign: 'right' }}
                                        value={baseSalary === '' ? '' : baseSalary} 
                                        onChange={(e) => {
                                            const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                            setBaseSalary(val);
                                            setPerDaySalary(val / 30);
                                        }} 
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>Duty Days</td>
                                <td>
                                    <input 
                                        type="number" 
                                        className="inline-input short" 
                                        value={dutyDays === '' ? '' : dutyDays} 
                                        onChange={(e) => setDutyDays(e.target.value === '' ? '' : parseInt(e.target.value))} 
                                    /> Days (Standard: {standardDutyDays})
                                </td>
                                <td className="amount">-</td>
                            </tr>
                            <tr>
                                <td>Paid Holidays</td>
                                <td>
                                    <input 
                                        type="number" 
                                        className="inline-input short" 
                                        value={holidaysCount === '' ? '' : holidaysCount} 
                                        onChange={(e) => setHolidaysCount(e.target.value === '' ? '' : parseInt(e.target.value))} 
                                    /> Days
                                </td>
                                <td className="amount">-</td>
                            </tr>
                            <tr>
                                <td>Paid Leaves (CL)</td>
                                <td>
                                    <input 
                                        type="number" 
                                        className="inline-input short" 
                                        value={paidLeaves === '' ? '' : paidLeaves} 
                                        onChange={(e) => setPaidLeaves(e.target.value === '' ? '' : parseInt(e.target.value))} 
                                    /> Day
                                </td>
                                <td className="amount">-</td>
                            </tr>
                            <tr>
                                <td>Unpaid Leaves</td>
                                <td>
                                    <input 
                                        type="number" 
                                        className="inline-input short" 
                                        value={unpaidLeaveDays === '' ? '' : unpaidLeaveDays} 
                                        onChange={(e) => setUnpaidLeaveDays(e.target.value === '' ? '' : parseInt(e.target.value))} 
                                    /> Days @ {Number(perDaySalary || 0).toFixed(2)}/day
                                </td>
                                <td className="amount negative">
                                    - <input 
                                        type="number" 
                                        className="inline-input text-right" 
                                        style={{ width: '80px', color: '#ef4444', textAlign: 'right' }}
                                        value={unpaidLeaveDeduction === '' ? '' : unpaidLeaveDeduction} 
                                        onChange={(e) => setUnpaidLeaveDeduction(e.target.value === '' ? '' : parseFloat(e.target.value))} 
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>Half Days</td>
                                <td>
                                    <input 
                                        type="number" 
                                        className="inline-input short" 
                                        value={halfDays === '' ? '' : halfDays} 
                                        onChange={(e) => setHalfDays(e.target.value === '' ? '' : parseInt(e.target.value))} 
                                    /> Half Days @ {Number((perDaySalary || 0) / 2).toFixed(2)}/half
                                </td>
                                <td className="amount negative">
                                    - <input 
                                        type="number" 
                                        className="inline-input text-right" 
                                        style={{ width: '80px', color: '#ef4444', textAlign: 'right' }}
                                        value={halfDayDeduction === '' ? '' : halfDayDeduction} 
                                        onChange={(e) => setHalfDayDeduction(e.target.value === '' ? '' : parseFloat(e.target.value))} 
                                    />
                                </td>
                            </tr>
                            {allowances > 0 && (
                                <tr>
                                    <td>Manual Allowances/Bonuses</td>
                                    <td>Admin Input</td>
                                    <td className="amount positive">+ {allowances.toFixed(2)}</td>
                                </tr>
                            )}
                            {manualDeductions > 0 && (
                                <tr>
                                    <td>Manual Deductions</td>
                                    <td>Admin Input</td>
                                    <td className="amount negative">- {manualDeductions.toFixed(2)}</td>
                                </tr>
                            )}
                            <tr className="total-row">
                                <td colSpan="2">Net Payable Salary</td>
                                <td className="amount">₹ {exactNetSalary.toFixed(2)}</td>
                            </tr>
                            {roundOff !== 0 && (
                                <tr>
                                    <td colSpan="2">Round Off</td>
                                    <td className={`amount ${roundOff > 0 ? 'positive' : 'negative'}`}>
                                        {roundOff > 0 ? '+' : '-'} {Math.abs(roundOff).toFixed(2)}
                                    </td>
                                </tr>
                            )}
                            {roundOff !== 0 && (
                                <tr className="total-row" style={{ backgroundColor: '#eef2ff' }}>
                                    <td colSpan="2">Final Payable Amount</td>
                                    <td className="amount">₹ {netSalary.toFixed(2)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Right Side: Calculator & Action */}
                <div className="ps-side">
                    <div className="ps-card calculator-form">
                        <h2>Manual Adjustments</h2>
                        
                        <div className="form-group">
                            <label>Allowances / Bonuses (₹)</label>
                            <input 
                                type="number" 
                                min="0" 
                                value={allowances || ''}
                                onChange={(e) => setAllowances(parseFloat(e.target.value) || 0)}
                                placeholder="Enter extra earnings"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Other Deductions (₹)</label>
                            <input 
                                type="number" 
                                min="0" 
                                value={manualDeductions || ''}
                                onChange={(e) => setManualDeductions(parseFloat(e.target.value) || 0)}
                                placeholder="Enter deductions (penalties, etc.)"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Date of Issue</label>
                            <input 
                                type="date" 
                                value={issueDate || ''}
                                onChange={(e) => setIssueDate(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="net-payable-box">
                        {roundOff !== 0 && (
                            <div className="round-off-info" style={{ fontSize: '13px', color: '#666', marginBottom: '8px', textAlign: 'center' }}>
                                Net: ₹{exactNetSalary.toFixed(2)} | Round Off: {roundOff > 0 ? '+' : ''}{roundOff.toFixed(2)}
                            </div>
                        )}
                        <span>Final Payable Amount</span>
                        <h3>{formatCurrency(netSalary)}</h3>
                    </div>

                    <div className="action-buttons">
                        <button 
                            className="btn-process" 
                            onClick={handleProcessPayment}
                            disabled={processing}
                        >
                            {processing ? 'Processing...' : (isEditMode ? '💾 Update Payment' : '💳 Process Payment')}
                        </button>
                        <button 
                            className="btn-preview" 
                            onClick={() => setShowSlipModal(true)}
                            disabled={processing}
                        >
                            👁️ Preview
                        </button>
                    </div>
                </div>
            </div>

            {showSlipModal && (
                <SalarySlipModal 
                    employee={employee} 
                    payroll={{month: monthParam, year: yearParam}} 
                    onClose={() => {
                        setShowSlipModal(false);
                        if (justPaid) {
                            navigate(`/admin/employees/${employee._id}`);
                        }
                    }} 
                    isAfterPayment={justPaid}
                />
            )}

            {showPaymentModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>{isEditMode ? 'Update Payment Details' : 'Process Payment'}</h2>
                        <form onSubmit={confirmPayment}>
                            <div className="form-group">
                                <label>Payment Mode / Transaction ID *</label>
                                <input 
                                    type="text" 
                                    value={transactionId || ''} 
                                    onChange={(e) => setTransactionId(e.target.value)} 
                                    placeholder="e.g. NEFT, UPI, Cash" 
                                    required 
                                />
                            </div>
                            <div className="form-group">
                                <label>Payment Date & Time *</label>
                                <input 
                                    type="datetime-local" 
                                    value={paymentDate || ''} 
                                    onChange={(e) => setPaymentDate(e.target.value)} 
                                    required 
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowPaymentModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={processing}>
                                    {processing ? 'Saving...' : (isEditMode ? 'Confirm & Update' : 'Confirm & Pay')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaySalary;
